import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  TextField,
  Button,
  Typography,
  Paper,
  Grid,
  CircularProgress,
  Alert,
  Snackbar,
  Chip,
  Box,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import { useAuth } from '../context/AuthContext';
import { useWorkspace } from '../context/WorkspaceContext';
import { api } from '../utils/api';

// TODO: Replace with a real subscription API / Stripe integration. This is a
// UI-only placeholder so the billing section is complete but inert.
const MOCK_BILLING = {
  plan: 'Free',
  seatsUsed: 3,
  seatsAllowed: 5,
  nextRenewal: 'December 1, 2026',
};

const AccountPage = () => {
  const { user, refreshUser, logout } = useAuth();
  const { currentOrganization, isOrgAdmin } = useWorkspace();
  const navigate = useNavigate();

  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [toast, setToast] = useState('');

  // Profile
  const [name, setName] = useState(user?.name || '');
  const [profileLoading, setProfileLoading] = useState(false);

  // Security
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordLoading, setPasswordLoading] = useState(false);

  // Danger zone
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState('');
  const [deleteLoading, setDeleteLoading] = useState(false);

  if (!user) {
    return <Typography>Please log in to view your account.</Typography>;
  }

  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    setProfileLoading(true);
    setError('');
    setSuccess('');
    try {
      await api.put('/users/me/profile', { name: name.trim() });
      await refreshUser();
      setSuccess('Profile updated successfully!');
    } catch (err) {
      setError(err.response?.data?.msg || err.message || 'Failed to update profile');
    }
    setProfileLoading(false);
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (newPassword !== confirmPassword) {
      setError('New password and confirmation do not match');
      return;
    }
    if (newPassword.length < 8) {
      setError('New password must be at least 8 characters long');
      return;
    }

    setPasswordLoading(true);
    try {
      await api.post('/users/me/change-password', { currentPassword, newPassword });
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setSuccess('Password updated successfully!');
    } catch (err) {
      setError(err.response?.data?.msg || err.message || 'Failed to change password');
    }
    setPasswordLoading(false);
  };

  const handleDeleteAccount = async () => {
    setDeleteLoading(true);
    setError('');
    setSuccess('');
    try {
      await api.delete('/users/me');
      logout();
      navigate('/');
    } catch (err) {
      setError(err.response?.data?.msg || err.message || 'Failed to delete account');
      setDeleteOpen(false);
    }
    setDeleteLoading(false);
  };

  const deleteConfirmationTarget = user.email;
  const canDelete = deleteConfirm === deleteConfirmationTarget || deleteConfirm === 'DELETE';

  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
      <Typography variant="h4" gutterBottom>
        Account
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

      {/* Profile */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h5" gutterBottom>
          Profile
        </Typography>
        <form onSubmit={handleProfileSubmit}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Name"
                name="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                margin="normal"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Email (Read-only)"
                name="email"
                value={user.email || ''}
                disabled
                margin="normal"
              />
            </Grid>
            <Grid item xs={12} sx={{ mt: 1 }}>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                disabled={profileLoading || !name.trim()}
              >
                {profileLoading ? <CircularProgress size={24} /> : 'Save Profile'}
              </Button>
            </Grid>
          </Grid>
        </form>
      </Paper>

      {/* Security */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h5" gutterBottom>
          Security
        </Typography>
        <form onSubmit={handlePasswordSubmit}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                type="password"
                label="Current Password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                margin="normal"
                autoComplete="current-password"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="password"
                label="New Password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                margin="normal"
                autoComplete="new-password"
                helperText="At least 8 characters."
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="password"
                label="Confirm New Password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                margin="normal"
                autoComplete="new-password"
              />
            </Grid>
            <Grid item xs={12} sx={{ mt: 1 }}>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                disabled={passwordLoading || !currentPassword || !newPassword || !confirmPassword}
              >
                {passwordLoading ? <CircularProgress size={24} /> : 'Change Password'}
              </Button>
            </Grid>
          </Grid>
        </form>
      </Paper>

      {/* Billing (org) — UI scaffold, mock data */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h5" gutterBottom>
          Billing
        </Typography>
        {!currentOrganization ? (
          <Typography variant="body2" color="text.secondary">
            Select an organization to manage billing.
          </Typography>
        ) : (
          <>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <Typography variant="h6">{currentOrganization.name}</Typography>
              <Chip label={MOCK_BILLING.plan} color="primary" size="small" />
            </Box>

            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <Typography variant="caption" color="text.secondary">Current plan</Typography>
                <Typography variant="body1">{MOCK_BILLING.plan}</Typography>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Typography variant="caption" color="text.secondary">Seats</Typography>
                <Typography variant="body1">
                  {MOCK_BILLING.seatsUsed} / {MOCK_BILLING.seatsAllowed} used
                </Typography>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Typography variant="caption" color="text.secondary">Next renewal</Typography>
                <Typography variant="body1">{MOCK_BILLING.nextRenewal}</Typography>
              </Grid>
            </Grid>

            <Divider sx={{ my: 2 }} />

            {isOrgAdmin() ? (
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Button variant="contained" onClick={() => setToast('Billing is coming soon')}>
                  Upgrade plan
                </Button>
                <Button variant="outlined" onClick={() => setToast('Billing is coming soon')}>
                  Manage billing
                </Button>
              </Box>
            ) : (
              <Typography variant="caption" color="text.secondary">
                Billing is managed by your organization owner.
              </Typography>
            )}
          </>
        )}
      </Paper>

      {/* Danger zone */}
      <Paper sx={{ p: 3, mb: 3, borderColor: 'error.main', borderWidth: 1, borderStyle: 'solid' }}>
        <Typography variant="h5" gutterBottom color="error">
          Danger zone
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Permanently delete your account and all associated data. This cannot be undone.
        </Typography>
        <Button variant="outlined" color="error" onClick={() => setDeleteOpen(true)}>
          Delete account
        </Button>
      </Paper>

      <Dialog open={deleteOpen} onClose={() => setDeleteOpen(false)}>
        <DialogTitle>Delete account</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            This will permanently delete your account. To confirm, type your email
            (<strong>{deleteConfirmationTarget}</strong>) or <strong>DELETE</strong> below.
          </DialogContentText>
          <TextField
            fullWidth
            label="Confirm"
            value={deleteConfirm}
            onChange={(e) => setDeleteConfirm(e.target.value)}
            autoComplete="off"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteOpen(false)} disabled={deleteLoading}>
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={handleDeleteAccount}
            disabled={!canDelete || deleteLoading}
          >
            {deleteLoading ? <CircularProgress size={24} /> : 'Delete account'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!toast}
        autoHideDuration={3000}
        onClose={() => setToast('')}
        message={toast}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Container>
  );
};

export default AccountPage;
